import React, { useState } from 'react';
import Modal from './Modal';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUp: (email: string, password: string) => Promise<any>;
  onSignIn: (email: string, password: string) => Promise<any>;
  user: any;
  onSignOut: () => Promise<any>;
}

const AuthModal = React.memo(function AuthModal({ 
  isOpen, 
  onClose, 
  onSignUp, 
  onSignIn, 
  user, 
  onSignOut 
}: AuthModalProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');

  // Reset signup success state when switching modes
  const handleModeChange = (newMode: 'signin' | 'signup') => {
    setMode(newMode);
    setSignupSuccess(false);
    setError('');
  };

  // Handle modal close with state reset
  const handleClose = () => {
    setSignupSuccess(false);
    setError('');
    setEmail('');
    setPassword('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        const { error } = await onSignUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          // Show email verification message instead of closing
          setSignupEmail(email);
          setSignupSuccess(true);
          setPassword('');
        }
      } else {
        const { error } = await onSignIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          handleClose();
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await onSignOut();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Sign out failed');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification success message
  if (signupSuccess) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Check Your Email" size="sm">
        <div className="space-y-4 text-center">
          <div className="text-5xl">
            ✉️
          </div>
          <div className="space-y-2">
            <p className="text-gray-700 dark:text-gray-200">
              We've sent a verification link to:
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {signupEmail}
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please check your inbox and click the link to complete your account setup. 
            The link will expire in 24 hours.
          </p>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Got it
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Didn't receive the email? Check your spam folder.
          </p>
        </div>
      </Modal>
    );
  }

  if (user) {
    // User is signed in - show account info
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Account" size="sm">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Signed in as
            </div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {user.email}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
              ☁️ Cloud sync enabled
            </div>
          </div>
          
          <button
            onClick={handleSignOut}
            disabled={loading}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Signing out...' : 'Sign Out'}
          </button>
          
          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}
        </div>
      </Modal>
    );
  }

  // User is not signed in - show auth form
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cloud Sync" size="sm">
      <div className="space-y-4">
        <div className="text-sm text-gray-600 dark:text-gray-300 text-center">
          Sign up to sync your session data across devices
        </div>
        
        <div className="flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
          <button
            type="button"
            onClick={() => handleModeChange('signin')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'signin'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('signup')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'signup'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="your@email.com"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Loading...' : mode === 'signin' ? 'Sign In' : 'Sign Up'}
          </button>
        </form>
        
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {mode === 'signup' 
            ? 'Your existing session data will be synced to the cloud automatically.'
            : 'Don\'t have an account? Click Sign Up above.'
          }
        </div>
      </div>
    </Modal>
  );
});

export default AuthModal;